import { XMarkIcon } from "@heroicons/react/24/outline";
import { useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useClickOutsideBlock } from "../../helpers/useClickOutside";
import { ROUTES } from "../../consts/general";

export const CategoryDeleteInfoModal = ({
    isOpen,
    onClose,
    categoryName,
    budgets,
    isUsedInForecastSettings = false,
}) => {
    const modalRef = useRef(null);
    const navigate = useNavigate();
    useClickOutsideBlock(modalRef, onClose);

    if (!isOpen) return null;

    const budgetCount = budgets?.length || 0;

    const handleBudgetClick = (budgetId) => {
        navigate(`${ROUTES.BUDGET}?id=${budgetId}`);
        onClose();
    };

    return (
        <div aria-hidden="true" className="overflow-y-auto overflow-x-hidden fixed inset-0 z-[60] justify-center items-center">
            <div className="bg-gray-700 fixed inset-0 z-[60] opacity-30" />
            <div ref={modalRef} className="relative p-5 w-full max-w-2xl max-h-full bg-white border fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-[70] rounded-md">
                <div className="flex justify-end">
                    <button 
                        onClick={onClose}
                        className="inline-flex items-center p-1 border border-transparent rounded-md shadow-sm text-white bg-primary hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                    >
                        <XMarkIcon className="h-4 w-4" aria-hidden="true" />
                    </button>
                </div>
                <div className="py-5">
                    <h4 className="text-xl font-semibold mb-4">
                        Cannot Delete Category
                    </h4>
                    <div className="space-y-4">
                        <p className="text-gray-700">
                            The category <span className="font-semibold">"{categoryName}"</span> cannot be deleted because it is still in use
                            {budgetCount > 0 && isUsedInForecastSettings
                                ? " by budgets and forecast settings."
                                : budgetCount > 0
                                    ? ` in ${budgetCount} ${budgetCount === 1 ? 'budget' : 'budgets'}.`
                                    : " in forecast settings."}
                        </p>

                        {budgetCount > 0 && (
                            <div>
                                <p className="text-sm font-medium text-gray-700 mb-2">
                                    Budgets using this category:
                                </p>
                                <ul className="list-disc list-inside space-y-1">
                                    {budgets.map((budget) => (
                                        <li key={budget.id}>
                                            <button
                                                onClick={() => handleBudgetClick(budget.id)}
                                                className="text-blue-600 hover:text-blue-800 hover:underline text-left"
                                            >
                                                {budget.name}
                                            </button>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}

                        {isUsedInForecastSettings && (
                            <div className="bg-purple-50 border border-purple-200 rounded-md p-4">
                                <p className="text-sm text-purple-800">
                                    <span className="font-semibold">Forecast Planning Settings:</span> This category is currently selected in forecast settings and must be replaced there before it can be deleted.
                                </p>
                            </div>
                        )}

                        {budgetCount > 0 && (
                            <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
                                <p className="text-sm text-blue-800">
                                    <span className="font-semibold">To delete this category:</span> You must move all line items from this category to another category within the budgets listed above. Once all line items have been reassigned, you can return to this page and delete the category.
                                </p>
                            </div>
                        )}
                    </div>
                </div>
                <div className="flex justify-end gap-2 pt-4 border-t">
                    <button 
                        onClick={onClose}
                        className="min-w-20 inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary sm:mt-0 sm:w-auto sm:text-sm"
                    >
                        Close
                    </button>
                </div>
            </div>
        </div>
    );
};
